import {
  ITymberEcommStore,
  getPaymentOptions,
  enableCash,
  disableCash,
  enableMerrco,
  disableMerrco,
  IPaymentIntegration,
  updateStore,
} from '@getgreenline/blaze-ecomm'
import Endpoints from '../../../../constants/Endpoints'
import { Spin, Switch } from 'antd'
import { Alert, Button, Checkbox, Col, Drawer, message, Row, Typography } from 'antd-v4'
import { inject } from 'mobx-react'
import React, { Dispatch, FC, SetStateAction, useEffect, useMemo, useState } from 'react'
import { COLORS } from '../../../../constants/Colors'
import { CurrentCompanyStore } from '../../../../stores/CurrentCompanyStore'
import { ConnectMerrcoModal } from './ConnectMerrcoModal'
import { capitalizeFirstLetter, parseErrorMsg } from '../../../../utilities/helpers'
import './styles.scss'
import { CheckboxChangeEvent } from 'antd-v4/lib/checkbox'
import { ConfigureEcomDeliveryModal } from './ConfigureEcomDeliveryModal'
import { LocationWithConfig } from './EcommerceSettingModels'
import { ConfigureEcomDelivery } from './ConfigureEcomDelivery'
import { observer } from 'mobx-react-lite'

interface ConfigurationDrawerProps {
  setSelectedLocation: Dispatch<SetStateAction<LocationWithConfig | null>>
  selectedLocation: LocationWithConfig
  locallyUpdateConfig: (updatedConfig: ITymberEcommStore) => void
  currentCompanyStore?: CurrentCompanyStore
  onlineOrderingIsEnabled: boolean
  setConnectMerrcoMessage: Dispatch<SetStateAction<string | null>>
  canUseKMLFiles: boolean | null
}

enum PaymentMethod {
  CASH = 'cash',
  MERRCO = 'merrco',
}

const _ConfigurationDrawer: FC<ConfigurationDrawerProps> = observer(
  ({
    setSelectedLocation,
    selectedLocation,
    currentCompanyStore,
    setConnectMerrcoMessage,
    canUseKMLFiles,
  }: ConfigurationDrawerProps) => {
    const { config } = selectedLocation
    const company = currentCompanyStore?.company

    useEffect(() => {
      async function fetchPaymentOptions() {
        if (!company?.id) return

        try {
          const paymentOptions = await getPaymentOptions(
            Endpoints.AUTHZ_GATEWAY_URL,
            company?.id.toString(),
            selectedLocation.id.toString(),
          )
          setMerrcoIntegrationData(paymentOptions.data.paymentIntegration)
          setPayAtStoreEnabled(
            paymentOptions?.data.paymentOptions.find(
              (i) => i.attributes.paymentOption === PaymentMethod.CASH,
            )?.attributes.isActive,
          )

          setMerrcoEnabled(
            paymentOptions?.data.paymentOptions.find(
              (i) => i.attributes.paymentOption === PaymentMethod.MERRCO,
            )?.attributes.isActive,
          )

          setMerrcoCofigured(paymentOptions.data.paymentIntegration.attributes.publicKey === null)
        } catch (error) {
          message.error(parseErrorMsg(error) || 'An error occurred while loading payment options')
        } finally {
          setIsWaitingPaymentOptionsFetch(false)
        }
      }

      fetchPaymentOptions()
    }, [])

    const [isWaitingPaymentOptionsFetch, setIsWaitingPaymentOptionsFetch] = useState<boolean>(true)
    const [isPayAtStoreEnabled, setPayAtStoreEnabled] = useState<boolean>()
    const [isMerrcoCofigured, setMerrcoCofigured] = useState<boolean>()
    const [isMerrcoEnabled, setMerrcoEnabled] = useState<boolean>()
    const [isMerrcoLoading, setMerrcoLoading] = useState<boolean>(false)
    const [isConnectMerrcoModalVisible, setIsConnectMerrcoModalVisible] = useState<boolean>(false)
    const [isConfigureEcomDeliveryModalVisible, setIsConfigureEcomDeliveryModalVisible] =
      useState<boolean>(false)
    const [isPayAtStoreLoading, setIsPayAtStoreLoading] = useState<boolean>(false)
    const [merrcoIntegrationData, setMerrcoIntegrationData] = useState<IPaymentIntegration>()
    const [isDeliveryLoading, setIsDeliveryLoading] = useState(false)

    const configureEcomDelivery = useMemo(() => {
      return new ConfigureEcomDelivery(selectedLocation, canUseKMLFiles)
    }, [selectedLocation, canUseKMLFiles])

    const isEcomDeliveryConfigured = useMemo(() => {
      return configureEcomDelivery.isEcomDeliveryConfigured
    }, [configureEcomDelivery.isLoading])

    const lowerCaseStatus = config?.attributes.setupStatus || 'processing'
    const isDeliveryToggleChecked = selectedLocation?.isEcommDeliveryEnabled

    const getSetupStatus = (lowerCaseSetupStatus: string) => {
      const setupStatusCapitalized = capitalizeFirstLetter(lowerCaseSetupStatus)
      return lowerCaseSetupStatus === 'failed'
        ? `${setupStatusCapitalized} (${config?.attributes?.setupFailedAtStep || ''})`
        : setupStatusCapitalized
    }

    const locationData = {
      'Company name': currentCompanyStore?.company?.name,
      'Location name': selectedLocation.name,
      'Setup status': getSetupStatus(lowerCaseStatus),
    }

    const onChangePayAtStore = async (event: CheckboxChangeEvent) =>
      updatePaymentOptionStatus(
        event,
        enableCash,
        disableCash,
        setPayAtStoreEnabled,
        setIsPayAtStoreLoading,
      )

    const onChangeMerrco = async (event: CheckboxChangeEvent) =>
      updatePaymentOptionStatus(
        event,
        enableMerrco,
        disableMerrco,
        setMerrcoEnabled,
        setMerrcoLoading,
      )

    const updatePaymentOptionStatus = async (
      event: CheckboxChangeEvent,
      enablePaymentOption: Function,
      disablePaymentOption: Function,
      setPaymentOptionEnabled: Function,
      setPaymentOptionLoading: Function,
    ) => {
      if (!company) return

      setPaymentOptionLoading(true)

      if (event.target.checked) {
        try {
          await enablePaymentOption(
            Endpoints.AUTHZ_GATEWAY_URL,
            company?.id.toString(),
            selectedLocation.id.toString(),
          )
        } catch (error) {
          message.error(parseErrorMsg(error) || 'An error occurred while enabling payment option')
        }
      } else {
        try {
          await disablePaymentOption(
            Endpoints.AUTHZ_GATEWAY_URL,
            company?.id.toString(),
            selectedLocation.id.toString(),
          )
        } catch (error) {
          message.error(parseErrorMsg(error) || 'An error occurred while disabling payment option')
        }
      }

      setPaymentOptionEnabled(event.target.checked)
      setPaymentOptionLoading(false)
    }

    const titleSectionTyle = {
      fontSize: 14,
      color: COLORS.black,
      marginBottom: 12,
    }

    const isSetupProcessing = lowerCaseStatus === 'processing'

    const toggleDelivery = async () => {
      if (!selectedLocation) return
      setIsDeliveryLoading(true)
      try {
        if (!selectedLocation.config?.attributes.isActive) {
          throw new Error('Ecom is disabled for this location')
        }
        await updateStore(
          Endpoints.AUTHZ_GATEWAY_URL,
          `${selectedLocation.companyId}`,
          `${selectedLocation.id}`,
          {
            isActive: selectedLocation.config.attributes.isActive,
            allowDeliveries: !isDeliveryToggleChecked,
          },
        )
        await currentCompanyStore?.editLocation({
          id: selectedLocation.id,
          isEcommDeliveryEnabled: !isDeliveryToggleChecked,
        })
        setSelectedLocation({
          ...selectedLocation,
          isEcommDeliveryEnabled: !isDeliveryToggleChecked,
        })
        message.success(`Delivery for ${selectedLocation?.name} successfully updated.`, 2)
      } catch (error) {
        message.error(
          parseErrorMsg(error) ||
            `There was an error while updating Delivery for ${selectedLocation?.name}`,
        )
      } finally {
        setIsDeliveryLoading(false)
      }
    }

    return (
      <Drawer
        className='ecommerce-configuration-drawer'
        title='BLAZE ECOM - Configuration'
        width={400}
        visible={true}
        onClose={() => setSelectedLocation(null)}
      >
        <Row style={{ flex: 1 }}>
          <Typography.Paragraph>Configure {selectedLocation.name}</Typography.Paragraph>
          <Row>
            {Object.keys(locationData).map((key, index) => {
              return (
                <Row
                  key={key}
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    marginTop: `${index > 0 && '8px'}`,
                  }}
                >
                  <Col flex='120px'>{key}</Col>
                  <Col flex='1'>{locationData[key]}</Col>
                </Row>
              )
            })}
          </Row>
          {lowerCaseStatus === 'failed' ? (
            <Row style={{ marginTop: 8 }}>
              <Alert
                message={`Setup failed. Please contact support for help. (Store ID ${config?.id})`}
                type='error'
                showIcon
              />
            </Row>
          ) : (
            <>
              <Row className='my-4'>
                <Button
                  key='preview'
                  disabled={isSetupProcessing}
                  href={config?.attributes.site?.primaryMenuUrl}
                  target='_blank'
                  size='large'
                >
                  Preview store
                </Button>
              </Row>
              <>
                <Typography.Title
                  level={5}
                  style={{
                    ...titleSectionTyle,
                    fontWeight: 500,
                  }}
                >
                  Payment methods
                </Typography.Title>
                {isWaitingPaymentOptionsFetch ? (
                  <Spin />
                ) : (
                  <>
                    {isPayAtStoreLoading ? (
                      <Spin />
                    ) : (
                      <Checkbox
                        checked={isPayAtStoreEnabled}
                        onChange={onChangePayAtStore}
                        style={{ userSelect: 'none' }}
                        disabled={isSetupProcessing}
                      >
                        <Typography.Text
                          style={{
                            fontSize: 14,
                            color: COLORS.black,
                          }}
                        >
                          Pay at store
                        </Typography.Text>
                        <Typography.Paragraph
                          style={{ fontSize: 12, marginLeft: 24, marginBottom: 0 }}
                        >
                          Allow customers to pay in store after placing a pickup order.
                        </Typography.Paragraph>
                      </Checkbox>
                    )}
                    <div>
                      {isMerrcoLoading ? (
                        <Spin />
                      ) : (
                        <>
                          <Checkbox
                            className=''
                            checked={isMerrcoEnabled}
                            onChange={onChangeMerrco}
                            disabled={isSetupProcessing || isMerrcoCofigured}
                          >
                            <Typography.Text
                              style={{
                                fontSize: 14,
                                color: COLORS.black,
                              }}
                            >
                              Pay online (Merrco)
                            </Typography.Text>
                            <Typography.Paragraph
                              className='mt-1 mb-1'
                              style={{
                                fontSize: 12,
                                marginLeft: 24,
                                marginBottom: 0,
                              }}
                            >
                              Process all online orders with Merrco.
                            </Typography.Paragraph>
                          </Checkbox>
                          <Row>
                            <a
                              style={{
                                fontSize: 12,
                                position: 'absolute',
                                left: '24px',
                                top: '-20px',
                                color: COLORS.blue500,
                                cursor: lowerCaseStatus === 'completed' ? 'pointer' : 'not-allowed',
                              }}
                              onClick={() =>
                                lowerCaseStatus === 'completed'
                                  ? setIsConnectMerrcoModalVisible(true)
                                  : null
                              }
                            >
                              Configure Merrco settings
                            </a>
                          </Row>
                        </>
                      )}
                    </div>
                  </>
                )}
              </>
            </>
          )}
          {selectedLocation?.config?.greenlineAttributes?.isDeliveryEnabled && (
            <>
              <Typography.Title
                level={5}
                style={{
                  ...titleSectionTyle,
                  marginTop: 20,
                  fontWeight: 500,
                }}
              >
                Delivery
              </Typography.Title>
              <Switch
                checked={isDeliveryToggleChecked}
                disabled={!isEcomDeliveryConfigured}
                loading={isDeliveryLoading || configureEcomDelivery.isLoading}
                onChange={toggleDelivery}
              />
              <Typography.Text
                style={{
                  fontSize: 14,
                  marginLeft: 15,
                  color: COLORS.black,
                }}
              >
                Enable delivery
              </Typography.Text>
              <Typography.Paragraph style={{ fontSize: 12, marginLeft: 60, marginBottom: 0 }}>
                Allow customers to place delivery orders.
              </Typography.Paragraph>
              <Row>
                <a
                  style={{
                    fontSize: 12,
                    marginLeft: 60,
                    color: COLORS.blue500,
                  }}
                  onClick={() => {
                    setIsConfigureEcomDeliveryModalVisible(true)
                  }}
                >
                  Configure delivery settings
                </a>
              </Row>
            </>
          )}
        </Row>
        <ConnectMerrcoModal
          visible={isConnectMerrcoModalVisible}
          closeModal={() => {
            setIsConnectMerrcoModalVisible(false)
          }}
          setConnectMerrcoMessage={setConnectMerrcoMessage}
          selectedLocation={selectedLocation}
          setSelectedLocation={setSelectedLocation}
          merrcoIntegrationData={merrcoIntegrationData}
        />
        <ConfigureEcomDeliveryModal
          visible={isConfigureEcomDeliveryModalVisible}
          onCloseModal={() => setIsConfigureEcomDeliveryModalVisible(false)}
          configureEcomDelivery={configureEcomDelivery}
          setSelectedLocation={setSelectedLocation}
          isDeliveryLoading={isDeliveryLoading}
        />
      </Drawer>
    )
  },
)

export const ConfigurationDrawer = inject('currentCompanyStore')(_ConfigurationDrawer)
